import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import API_CONFIG from '../config/api';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';
import { SkeletonEventCard } from './Skeleton';
import { formatNombre, formatPeso, formatPesoCategoriaLista, formatCategoriaEdad } from '../utils/formatoParticipantes';
import { parseFechaLocal } from '../utils/fechas';
import '../styles/events/EventsPage.css';

// Sección "Ver participantes" en el detalle del evento: oculta por ahora.
// Cambiar a true para volver a mostrarla.
const MOSTRAR_PARTICIPANTES = false;

const apiBase = API_CONFIG.BASE_URL || process.env.REACT_APP_API_URL || '';

const getPrecioInscripcionLabel = (e) => {
  const det = e?.costos_inscripcion_detalle && typeof e.costos_inscripcion_detalle === 'object'
    ? e.costos_inscripcion_detalle
    : {};
  const vals = Object.values(det)
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
  const minDet = vals.length ? Math.min(...vals) : null;
  const legacy = Number(e?.costo_inscripcion || 0);

  const monto = (minDet && minDet > 0) ? minDet : (legacy > 0 ? legacy : 0);
  if (monto <= 0) return 'Gratis';
  return `Desde $${Number(monto).toLocaleString('es-CL')}`;
};

const mapEventoFromApi = (e) => ({
  id: e.id,
  nombre: e.nombre,
  descripcion: e.descripcion,
  tipo: e.tipo_evento,
  estado: e.estado,
  fecha: e.fecha_inicio,
  fechaFin: e.fecha_fin,
  ubicacion: (e.ubicacion || [e.ciudad, e.region].filter(Boolean).join(', ')) || '',
  region: e.region,
  ciudad: e.ciudad,
  fechaLimiteInscripcion: e.fecha_limite_inscripcion || null,
  cuposMaximos: e.cupos_maximos,
  cuposDisponibles: e.cupos_disponibles,
  precioInscripcion: getPrecioInscripcionLabel(e),
  foto: e.imagen_url,
  divisiones: Array.isArray(e.divisiones) ? e.divisiones : [],
  modalidad: Array.isArray(e.modalidad) ? e.modalidad : [],
  incluye_tested: !!e.incluye_tested,
  incluye_untested: !!e.incluye_untested,
  categorias_incluidas: Array.isArray(e.categorias_incluidas) ? e.categorias_incluidas : [],
  costos_inscripcion_detalle: e.costos_inscripcion_detalle || {},
  modalidad_extra_habilitada: !!e.modalidad_extra_habilitada,
  modalidad_extra_monto: Number(e.modalidad_extra_monto || 0),
  edad_extra_habilitada: !!e.edad_extra_habilitada,
  formulario_inscripcion_tipo: e.formulario_inscripcion_tipo || null,
  formulario_inscripcion_url: e.formulario_inscripcion_url || null,
});

const EventsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('id');
  const [activeTab, setActiveTab] = useState('proximos');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventosDestacados, setEventosDestacados] = useState([]);
  const [eventosProximos, setEventosProximos] = useState([]);
  const [eventosPasados, setEventosPasados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_events']));
  const [participantes, setParticipantes] = useState([]);
  const [loadingParticipantes, setLoadingParticipantes] = useState(false);
  const [showParticipantes, setShowParticipantes] = useState(false);

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_events || null))
      .catch(() => setHeroImage(null));
  }, []);

  /* =========================
     DESTACADOS (solo los que el admin marcó como destacado)
     ========================= */
  useEffect(() => {
    if (!apiBase) return;
    fetch(`${apiBase}/api/eventos/destacados`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setEventosDestacados(Array.isArray(data) ? data.map(mapEventoFromApi) : []);
      })
      .catch(() => setEventosDestacados([]));
  }, []);

  /* =========================
     TODOS LOS EVENTOS (lista = todos los publicados) → Próximos (fecha_fin >= hoy) y Pasados (fecha_fin < hoy)
     ========================= */
  useEffect(() => {
    if (!apiBase) {
      setLoading(false);
      return;
    }
    fetch(`${apiBase}/api/eventos/lista?ts=${Date.now()}`, { cache: 'no-store' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const proximos = [];
        const pasados = [];
        list.forEach(e => {
          const evento = mapEventoFromApi(e);
          const fechaFinEvento = parseFechaLocal(e.fecha_fin) || new Date(e.fecha_fin);
          fechaFinEvento.setHours(0, 0, 0, 0);
          if (fechaFinEvento >= hoy) {
            proximos.push(evento);
          } else {
            pasados.push(evento);
          }
        });
        setEventosProximos(proximos);
        setEventosPasados(pasados);
      })
      .catch(err => {
        console.error('Error cargando eventos', err);
        setEventosProximos([]);
        setEventosPasados([]);
      })
      .finally(() => setLoading(false));
  }, []);

  /* =========================
     HELPERS
     ========================= */
  const formatFecha = (fecha) =>
    (parseFechaLocal(fecha) || new Date(fecha)).toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

  const getColorByTipo = (tipo = '') => {
    if (tipo === 'competencia') return '#D52B1E';
    if (tipo === 'torneo') return '#0039A6';
    if (tipo === 'meet') return '#059669';
    if (tipo === 'exhibicion') return '#d97706';
    return '#374151';
  };

  const labelTipo = (tipo) => {
    if (tipo === 'competencia') return 'Competencia';
    if (tipo === 'torneo') return 'Torneo';
    if (tipo === 'meet') return 'Meet';
    if (tipo === 'exhibicion') return 'Exhibición';
    return tipo;
  };

  const loadParticipantes = (eventoId) => {
    if (!apiBase || !eventoId) return;
    setLoadingParticipantes(true);
    setParticipantes([]);
    fetch(`${apiBase}/api/eventos/${eventoId}/participantes`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setParticipantes(Array.isArray(data) ? data : []);
      })
      .catch(() => setParticipantes([]))
      .finally(() => setLoadingParticipantes(false));
  };

  const toggleParticipantes = () => {
    if (!showParticipantes && selectedEvent) {
      if (participantes.length === 0 && !loadingParticipantes) loadParticipantes(selectedEvent.id);
    }
    setShowParticipantes(prev => !prev);
  };

  useEffect(() => {
    if (selectedEvent) return;
    setShowParticipantes(false);
    setParticipantes([]);
  }, [selectedEvent]);

  /* =========================
     Abrir detalle si viene ?id=X desde home/links
     ========================= */
  useEffect(() => {
    if (!eventIdFromUrl || loading) return;
    const allEvents = [...eventosDestacados, ...eventosProximos, ...eventosPasados];
    const idStr = String(eventIdFromUrl);
    const found = allEvents.find(e => String(e.id) === idStr);
    if (found) setSelectedEvent(found);
  }, [eventIdFromUrl, loading, eventosDestacados, eventosProximos, eventosPasados]);

  /* =========================
     UI
     ========================= */
  const EventCard = ({ evento }) => (
    <button type="button" className="ev-card" onClick={() => setSelectedEvent(evento)}>
      <span className="ev-card-img">
        {evento.foto ? <img src={evento.foto} alt="" loading="lazy" /> : <span className="ev-card-noimg ach-display">ACHIPO</span>}
        <span className="ev-card-badge" style={{ background: getColorByTipo(evento.tipo) }}>
          {labelTipo(evento.tipo)}
        </span>
      </span>
      <span className="ev-card-body">
        <span className="ach-display ev-card-title">{evento.nombre}</span>
        <span className="ev-card-date">{formatFecha(evento.fecha)}</span>
        {evento.ubicacion && <span className="ev-card-place">{evento.ubicacion}</span>}
        <span className="ev-card-cta">Ver detalle →</span>
      </span>
    </button>
  );

  const listaActual = activeTab === 'proximos' ? eventosProximos : eventosPasados;

  return (
    <div className="ev-page">
      <Header />
      <PageHero
        eyebrow="Compite"
        imageUrl={heroImage}
        title="Calendario de Eventos"
        subtitle="Competencias oficiales de ACHIPO"
      />
      <div className="ev-wrap">

        {/* =========================
            DESTACADOS (solo los que el admin marcó como destacado)
           ========================= */}
        {!selectedEvent && !loading && eventosDestacados.length > 0 && (
          <section className="ev-section ev-section--featured" aria-labelledby="ev-destacados">
            <span className="ach-eyebrow-outline ev-eyebrow">Destacados</span>
            <h2 id="ev-destacados" className="ach-display ev-section-title">Eventos destacados</h2>
            <div className="ev-grid">
              {eventosDestacados.map(evento => (
                <EventCard key={evento.id} evento={evento} />
              ))}
            </div>
          </section>
        )}

        {/* =========================
            LISTADO
           ========================= */}
        {!selectedEvent && (
          <section className="ev-section" aria-labelledby="ev-listado">
            <div className="ev-list-head">
              <h2 id="ev-listado" className="ach-display ev-section-title">
                {activeTab === 'proximos' ? 'Próximos eventos' : 'Eventos pasados'}
              </h2>
              <div className="ev-tabs" role="group" aria-label="Filtrar eventos">
                {['proximos', 'pasados'].map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={activeTab === tab ? 'on' : ''}
                    aria-pressed={activeTab === tab}
                  >
                    {tab === 'proximos' ? 'Próximos' : 'Pasados'}
                  </button>
                ))}
              </div>
            </div>

            {loading && (
              <div className="ev-grid">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonEventCard key={i} />
                ))}
              </div>
            )}

            {!loading && listaActual.length === 0 && (
              <div className="ev-empty">
                <p>No hay eventos {activeTab === 'proximos' ? 'próximos' : 'pasados'}.</p>
                {activeTab === 'proximos' && (
                  <p className="ev-empty-note">
                    Solo se muestran eventos <strong>publicados</strong>. Si hay postulaciones pendientes, un administrador debe aprobarlas en el panel de administración (Eventos → Postulaciones).
                  </p>
                )}
              </div>
            )}

            {!loading && listaActual.length > 0 && (
              <div className="ev-grid">
                {listaActual.map(evento => (
                  <EventCard key={evento.id} evento={evento} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* =========================
            DETALLE
           ========================= */}
        {selectedEvent && (
          <div className="ev-detail">
            <button
              type="button"
              onClick={() => setSelectedEvent(null)}
              className="ach-pill ach-pill--dark ach-pill--sm ev-back"
            >
              ← Volver a eventos
            </button>

            <div className="ev-detail-top">
              <div className="ev-detail-poster">
                {selectedEvent.foto ? (
                  <img src={selectedEvent.foto} alt={`Afiche ${selectedEvent.nombre}`} />
                ) : (
                  <span className="ach-display ev-card-noimg">ACHIPO</span>
                )}
              </div>

              <div className="ev-detail-info">
                <span className="ev-detail-type" style={{ background: getColorByTipo(selectedEvent.tipo) }}>
                  {labelTipo(selectedEvent.tipo)}
                </span>
                <h2 className="ach-display ev-detail-title">{selectedEvent.nombre}</h2>
                <p className="ev-detail-meta">
                  {formatFecha(selectedEvent.fecha)}
                  {selectedEvent.ubicacion ? ` · ${selectedEvent.ubicacion}` : ''}
                </p>
                {selectedEvent.descripcion && <p className="ev-detail-desc">{selectedEvent.descripcion}</p>}

                <div className="ev-detail-ctas">
                  {(() => {
                    const tipo = String(selectedEvent.formulario_inscripcion_tipo || '').trim().toLowerCase();
                    const rawUrl = String(selectedEvent.formulario_inscripcion_url || '').trim();
                    const isExternal = tipo === 'externo' && !!rawUrl;
                    const finalUrl = isExternal
                      ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`)
                      : '';

                    return (
                      <>
                        <button
                          type="button"
                          className="ach-pill ach-pill--grad"
                          onClick={() => {
                            if (isExternal) {
                              window.open(finalUrl, '_blank', 'noreferrer');
                            } else {
                              navigate(`/events/${selectedEvent.id}/formulario`);
                            }
                          }}
                        >
                          Inscribirse
                        </button>
                        {isExternal && (
                          <a
                            href={finalUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="ach-pill ach-pill--dark"
                          >
                            Abrir formulario externo
                          </a>
                        )}
                      </>
                    );
                  })()}
                </div>

                {(() => {
                  const det = selectedEvent.costos_inscripcion_detalle || {};
                  const fmtCLP = (v) => `$${Number(v).toLocaleString('es-CL')}`;
                  const showUntested = !!selectedEvent.incluye_untested;
                  const showTested = !!selectedEvent.incluye_tested;
                  const uClubVal = Number(det.untested_con_club || 0);
                  const uIndVal = Number(det.untested_independiente || 0);
                  const tClubVal = Number(det.tested_con_club || 0);
                  const tIndVal = Number(det.tested_independiente || 0);
                  const insParts = [];
                  if (showUntested) {
                    if (Number.isFinite(uClubVal) && uClubVal > 0) insParts.push(`Untested con club: ${fmtCLP(uClubVal)}`);
                    if (Number.isFinite(uIndVal) && uIndVal > 0) insParts.push(`Untested independiente: ${fmtCLP(uIndVal)}`);
                  }
                  if (showTested) {
                    if (Number.isFinite(tClubVal) && tClubVal > 0) insParts.push(`Tested con club: ${fmtCLP(tClubVal)}`);
                    if (Number.isFinite(tIndVal) && tIndVal > 0) insParts.push(`Tested independiente: ${fmtCLP(tIndVal)}`);
                  }
                  const insText = insParts.length ? insParts.join(' | ') : (selectedEvent.precioInscripcion || 'Gratis');

                  const divisiones = (selectedEvent.divisiones || []).filter(d => d === 'Femenino' || d === 'Masculino');
                  const divisionesText = divisiones.length
                    ? divisiones.map(d => d === 'Femenino' ? 'Femenina' : 'Masculina').join(', ')
                    : '—';
                  const mod = selectedEvent.modalidad || [];
                  const modLabels = { raw: 'Raw', classic_raw: 'Classic Raw', single_ply: 'Single Ply', bench: 'Only Bench', deadlift: 'Only Deadlift', equipado: 'Equipado' };
                  const modText = mod.length ? mod.map(m => modLabels[m] || m).join(', ') : '—';
                  const compText = (selectedEvent.incluye_tested || selectedEvent.incluye_untested)
                    ? [selectedEvent.incluye_tested ? 'Tested' : null, selectedEvent.incluye_untested ? 'Untested' : null].filter(Boolean).join(' / ')
                    : null;
                  const catsText = (selectedEvent.categorias_incluidas && selectedEvent.categorias_incluidas.length > 0)
                    ? selectedEvent.categorias_incluidas.map((c) => ({ sub_junior: 'Sub junior', junior: 'Junior', open: 'Open', master: 'Master' }[c] || c)).join(', ')
                    : null;

                  const showModalidadExtra = !!selectedEvent.modalidad_extra_habilitada;
                  const extraMonto = Number(selectedEvent.modalidad_extra_monto || 0);
                  const extraMontoText = (Number.isFinite(extraMonto) && extraMonto > 0) ? fmtCLP(extraMonto) : null;
                  const showEdadExtra = !!selectedEvent.edad_extra_habilitada;

                  const Row = ({ label, children }) => (
                    <div className="ev-info-row">
                      <dt>{label}</dt>
                      <dd>{children}</dd>
                    </div>
                  );

                  return (
                    <dl className="ev-info">
                      {selectedEvent.fecha ? <Row label="Inicio">{formatFecha(selectedEvent.fecha)}</Row> : null}
                      {selectedEvent.fechaFin ? <Row label="Término">{formatFecha(selectedEvent.fechaFin)}</Row> : null}
                      {selectedEvent.fechaLimiteInscripcion ? <Row label="Límite inscripción">{formatFecha(selectedEvent.fechaLimiteInscripcion)}</Row> : null}
                      {selectedEvent.ubicacion ? <Row label="Ubicación">{selectedEvent.ubicacion}</Row> : null}
                      <Row label="Inscripción">{insText}</Row>
                      {(selectedEvent.cuposMaximos !== null && selectedEvent.cuposMaximos !== undefined && selectedEvent.cuposMaximos !== '') ? (
                        <Row label="Cupos máximos">{selectedEvent.cuposMaximos}</Row>
                      ) : null}
                      {(selectedEvent.cuposDisponibles !== null && selectedEvent.cuposDisponibles !== undefined && selectedEvent.cuposDisponibles !== '') ? (
                        <Row label="Cupos disponibles">{selectedEvent.cuposDisponibles}</Row>
                      ) : null}
                      <Row label="Divisiones">{divisionesText}</Row>
                      <Row label="Modalidades">{modText}</Row>
                      {compText ? <Row label="Competencia">{compText}</Row> : null}
                      {catsText ? <Row label="Categorías">{catsText}</Row> : null}
                      {showModalidadExtra ? (
                        <Row label="Modalidad extra">
                          <span className="ev-chip">Sí</span>
                          {extraMontoText ? <span className="ev-chip">{extraMontoText}</span> : null}
                        </Row>
                      ) : null}
                      {showEdadExtra ? (
                        <Row label="División edad extra">
                          <span className="ev-chip">Sí</span>
                          {extraMontoText ? <span className="ev-chip">{extraMontoText}</span> : null}
                        </Row>
                      ) : null}
                    </dl>
                  );
                })()}
              </div>
            </div>

            {MOSTRAR_PARTICIPANTES && (
            <section className="ev-participants">
              <div className="ev-participants-head">
                <h3 className="ach-display">Participantes</h3>
                <button
                  type="button"
                  onClick={toggleParticipantes}
                  className="ach-pill ach-pill--dark ach-pill--sm"
                  aria-expanded={showParticipantes}
                >
                  {showParticipantes ? 'Ocultar participantes' : 'Ver participantes'}
                </button>
              </div>
              {showParticipantes && (
                <div className="ev-participants-box">
                  {loadingParticipantes ? (
                    <p className="ev-muted">Cargando participantes…</p>
                  ) : participantes.length === 0 ? (
                    <p className="ev-muted">Aún no hay participantes inscritos.</p>
                  ) : (
                    <>
                      <p className="ev-participants-count">
                        {participantes.length} participante{participantes.length !== 1 ? 's' : ''}
                      </p>
                      <div className="ev-table-wrap">
                        <table className="ev-table">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>División</th>
                              <th>Categoría peso TESTED</th>
                              <th>Categoría peso UNTESTED</th>
                              <th>Categoría edad</th>
                              <th>Modalidad</th>
                              <th>Día</th>
                              <th>Tanda</th>
                            </tr>
                          </thead>
                          <tbody>
                            {participantes.map((p, idx) => {
                              const edadFmt = formatCategoriaEdad(p.categoria_edad);
                              const modalidadFmt = (p.modalidad || '').trim();
                              return (
                                <tr key={idx}>
                                  <td className="ev-table-name">{formatNombre(p.nombre)}</td>
                                  <td>{p.division || '—'}</td>
                                  <td>{formatPesoCategoriaLista(p.categoria_peso_tested, p.division)}</td>
                                  <td>{formatPesoCategoriaLista(p.categoria_peso_untested, p.division)}</td>
                                  <td>{edadFmt === '—' ? '—' : edadFmt.toUpperCase()}</td>
                                  <td>{modalidadFmt ? modalidadFmt.replace(/,/g, ', ').toUpperCase() : '—'}</td>
                                  <td>{p.dia_salida || '—'}</td>
                                  <td>{p.tanda || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EventsPage;
